import type { PromoRedirect } from "@internal/backend";

/**
 * A code that arrived in the URL (`?promo=CODE`) waits here until the cart, the pricing page or the
 * organization form can offer it. Local storage with a 30-day life: it has to survive the OAuth
 * bounce, an email-verification link that opens a new tab, and someone coming back tomorrow. It
 * holds nothing about the person, so it does not wait for analytics consent. The signup also files
 * the code on the account, which covers a new device.
 */
const STORAGE_KEY = "hs_promo_code";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

type Stored = { code: string; savedAt: number };

export const normalizePromoCode = (raw: string): string => raw.trim().toUpperCase().replace(/\s+/g, "");

const storage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const rememberPromoCode = (code: string): void => {
  const normalized = normalizePromoCode(code);
  if (!normalized) return;
  const entry: Stored = { code: normalized, savedAt: Date.now() };
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage full or blocked: the code is still in the URL for this visit.
  }
};

/** Reads `?promo=` off the current URL. Runs on every page so a deep link works too. */
export const rememberPromoCodeFromUrl = (): void => {
  if (typeof window === "undefined") return;
  const code = new URLSearchParams(window.location.search).get("promo");
  if (code) rememberPromoCode(code);
};

export const getRememberedPromoCode = (): string | null => {
  const raw = storage()?.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw) as Partial<Stored>;
    if (typeof entry.code !== "string" || typeof entry.savedAt !== "number") return null;
    if (Date.now() - entry.savedAt > TTL_MS) {
      forgetPromoCode();
      return null;
    }
    return entry.code;
  } catch {
    return null;
  }
};

export const forgetPromoCode = (): void => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to forget.
  }
};

/** Where a turned-down code should be entered instead, as a storefront path. */
export const promoRedirectHref = (redirect: PromoRedirect, code: string): string =>
  redirect === "cart" ? `/cart?promo=${encodeURIComponent(code)}` : `/redeem?promo=${encodeURIComponent(code)}`;

export const promoRedirectLabel = (redirect: PromoRedirect): string =>
  redirect === "cart" ? "Go to the cart" : "Redeem it on your account";
